import React, { useState, useEffect, useCallback } from 'react';
import { useUserManagement } from '../../hooks/useUserManagement';
import {
  UserTable,
  UserDetailsDialog,
  UserFilters,
  UserPagination,
  ResetPasswordDialog,
  ChangeRoleDialog,
  EditUserDialog
} from './users';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const { token, apiCall, showSuccess, showError, getRoleBadgeColor } = useUserManagement();
  
  // User list state
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(20);
  
  // Dialog states
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showUserDetailsDialog, setShowUserDetailsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('');
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    display_name: '',
    bio: '',
    phone: ''
  });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/api/admin/users?skip=${currentPage * pageSize}&limit=${pageSize}`;
      
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (roleFilter !== 'all') url += `&role=${roleFilter}`;
      
      const data = await apiCall('get', url);
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (error) {
      showError('Error', 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [apiCall, currentPage, pageSize, searchQuery, statusFilter, roleFilter, showError]);

  useEffect(() => {
    fetchUsers();
  }, [currentPage, statusFilter, roleFilter]);

  const handleSearch = () => {
    setCurrentPage(0);
    fetchUsers();
  };

  const handleApplyFilters = () => {
    setCurrentPage(0);
    fetchUsers();
  };

  const fetchUserDetails = async (userId) => {
    try {
      const data = await apiCall('get', `/api/admin/users/${userId}/full`);
      setUserDetails(data);
      setShowUserDetailsDialog(true);
    } catch (error) {
      showError('Error', 'Failed to fetch user details');
    }
  };

  const handleSuspend = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to suspend ${username}?`)) return;

    try {
      const formData = new URLSearchParams();
      formData.append('reason', 'Admin action');
      
      await apiCall('post', `/api/admin/users/${userId}/suspend`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      showSuccess('User suspended', `${username} has been suspended`);
      fetchUsers();
    } catch (error) {
      showError('Error', 'Failed to suspend user');
    }
  };

  const handleReactivate = async (userId, username) => {
    try {
      await apiCall('post', `/api/admin/users/${userId}/reactivate`, {});
      showSuccess('User reactivated', `${username} has been reactivated`);
      fetchUsers();
    } catch (error) {
      showError('Error', 'Failed to reactivate user');
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`⚠️ PERMANENT ACTION: Delete ${username} and ALL their data? This cannot be undone!`)) return;

    try {
      await apiCall('delete', `/api/admin/users/${userId}`);
      showSuccess('User deleted', `${username} and all associated data has been permanently deleted`);
      fetchUsers();
    } catch (error) {
      showError('Error', error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPasswordDialog(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      showError('Invalid password', 'Password must be at least 8 characters');
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('new_password', newPassword);

      await apiCall('post', `/api/admin/users/${selectedUser.id}/reset-password`, formData, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      
      showSuccess('Password reset', `Password for ${selectedUser.username} has been reset`);
      setShowResetPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      showError('Error', 'Failed to reset password');
    }
  };

  const openChangeRoleDialog = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowChangeRoleDialog(true);
  };

  const handleChangeRole = async () => {
    try {
      await apiCall('put', `/api/admin/users/${selectedUser.id}/role?role=${newRole}`, {});
      showSuccess('Role updated', `${selectedUser.username} is now ${newRole}`);
      setShowChangeRoleDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      showError('Error', 'Failed to change role');
    }
  };

  const openEditUserDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username || '',
      email: user.email || '',
      display_name: user.display_name || '',
      bio: user.bio || '',
      phone: user.phone || ''
    });
    setShowEditUserDialog(true);
  };

  const handleEditUser = async () => {
    try {
      await apiCall('patch', `/api/admin/users/${selectedUser.id}`, editForm);
      showSuccess('User updated', `${selectedUser.username}'s profile has been updated`);
      setShowEditUserDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      showError('Error', error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleExportCSV = async () => {
    try {
      let url = `/api/admin/users/export/csv`;
      const params = [];
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (roleFilter !== 'all') params.push(`role=${roleFilter}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await fetch(`${API_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await response.blob();
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `users_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showSuccess('Export complete', 'Users exported to CSV successfully');
    } catch (error) {
      showError('Error', 'Failed to export users');
    }
  };

  return (
    <div className="w-full">
      <UserFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        onSearch={handleSearch}
        onApplyFilters={handleApplyFilters}
        onExportCSV={handleExportCSV}
        totalUsers={totalUsers}
      />

      <UserTable
        users={users}
        loading={loading}
        getRoleBadgeColor={getRoleBadgeColor}
        onViewDetails={fetchUserDetails}
        onEditUser={openEditUserDialog}
        onResetPassword={openResetPasswordDialog}
        onChangeRole={openChangeRoleDialog}
        onSuspend={handleSuspend}
        onReactivate={handleReactivate}
        onDelete={handleDelete}
      />

      {!loading && (
        <UserPagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalUsers={totalUsers}
          pageSize={pageSize}
        />
      )}

      <UserDetailsDialog
        open={showUserDetailsDialog}
        onOpenChange={setShowUserDetailsDialog}
        userDetails={userDetails}
        getRoleBadgeColor={getRoleBadgeColor}
      />

      <ResetPasswordDialog
        open={showResetPasswordDialog}
        onOpenChange={setShowResetPasswordDialog}
        selectedUser={selectedUser}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onReset={handleResetPassword}
      />

      <ChangeRoleDialog
        open={showChangeRoleDialog}
        onOpenChange={setShowChangeRoleDialog}
        selectedUser={selectedUser}
        newRole={newRole}
        setNewRole={setNewRole}
        onChange={handleChangeRole}
      />

      <EditUserDialog
        open={showEditUserDialog}
        onOpenChange={setShowEditUserDialog}
        selectedUser={selectedUser}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={handleEditUser}
      />
    </div>
  );
};

export default UserManagement;
